import type { TFile } from '@conversed/shared/src/central/types/common.type';
import generalDocumentIcon from '@images/document.svg';
import excelIcon from '@images/excel-icon.svg';
import pdfIcon from '@images/pdf-icon.svg';
import webIcon from '@images/web-icon.svg';
import wordIcon from '@images/word-icon.svg';
import { objectDeepMapPromise } from './objectHelpers';

/**
 * Returns the appropriate file-type avatar/icon path for a file.
 * Checks MIME type first, then falls back to file extension.
 */
export const getFileAvatar = (
  fileType: string,
  fileName?: string,
  avatar?: string,
): string => {
  // Check by MIME type first
  if (fileType === 'application/pdf') {
    return pdfIcon;
  }

  if (fileType === 'text/html') {
    return webIcon;
  }

  if (
    fileType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    fileType === 'application/vnd.ms-excel'
  ) {
    return excelIcon;
  }

  if (
    fileType ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return wordIcon;
  }

  // Fallback: check by file extension when fileName is provided
  if (fileName) {
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (extension === 'aspx' || extension === 'html' || extension === 'htm') {
      return webIcon;
    }

    if (extension === 'pdf') {
      return pdfIcon;
    }

    if (extension === 'xlsx' || extension === 'xls' || extension === 'csv') {
      return excelIcon;
    }

    if (extension === 'docx' || extension === 'doc') {
      return wordIcon;
    }
  }

  return avatar || generalDocumentIcon;
};

// WARN: Deprecated
export function loadBase64FromReader(
  files: File | FileList,
): Promise<string | ArrayBuffer> {
  if (files instanceof FileList) {
    return loadBase64FromReader(files[0]);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(files);

    reader.onloadend = () => {
      if (reader.result) {
        resolve(reader.result);
      }
    };

    reader.onerror = () =>
      reject(new Error('There was an error uploading the file'));
  });
}

export function loadTFileFromReader(files: File | FileList): Promise<TFile> {
  if (files instanceof FileList) {
    return loadTFileFromReader(files[0]);
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(files);

    reader.onloadend = () => {
      if (reader.result && typeof reader.result === 'string') {
        const file = {
          name: files.name,
          type: files.type,
          size: files.size,
          base64: reader.result,
        };

        resolve(file);
      }
    };

    reader.onerror = () =>
      reject(new Error('There was an error uploading the file'));
  });
}

export const fileToBase64File = async (file: File) => ({
  name: file.name,
  type: file.type,
  size: file.size,
  base64: await loadBase64FromReader(file),
});

export const fileListToBase64File = (files: FileList) =>
  Promise.all(Array.from(files).map(fileToBase64File));

export const convertDataFilesToBase64Format = async (data: any) => {
  return objectDeepMapPromise(data, async (value: any) => {
    if (value instanceof FileList) {
      const file = await fileToBase64File(value[0]);
      return file;
    }

    return value;
  });
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  let size = bytes;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

export const getFileData = (key: string) => {
  return {
    extension: key.split('.').pop(),
    fileName: key.split('/').pop(),
    fileNameNoExtension: (key.split('/').pop() || '').split('.').pop(),
  };
};
